<template>
  <div class="course-rating">
    <div class="rating-header">
      <h3>课程互动</h3>
      <span class="rating-count">({{ratingInfo.ratingCount || 0}}人已评分)</span>
    </div>

    <!-- 评分按钮区域 - 仅登录用户显示 -->
    <div class="rating-buttons" v-if="isLoggedIn && (!userRating || !userRating.score)">
      <div class="rating-label">该内容对您的经营有帮助吗？</div>
      <div class="rating-options">
        <div
          v-for="item in ratingOptions"
          :key="item.score"
          class="rating-item"
          :class="{ active: hoveredScore === item.score, 'low-score': item.score <= 3 }"
          @click="handleRatingClick(item.score)"
          @mouseenter="hoveredScore = item.score"
          @mouseleave="hoveredScore = 0"
        >
          <span class="rating-emoji">{{item.emoji}}</span>
          <span class="rating-text">{{item.label}}</span>
        </div>
      </div>
    </div>

    <!-- 已评分状态 -->
    <div class="rating-submitted" v-if="isLoggedIn && userRating && userRating.score">
      <span class="rating-label">我的评分：<span style="color: #FD3374; font-weight: 700;">{{ userRating.score }}</span> 分</span>
      <span class="my-rating">
        <span class="rating-emoji">{{getRatingOption(userRating.score).emoji}}</span>
        <span class="rating-text">{{getRatingOption(userRating.score).label}}</span>
      </span>
      <span class="rating-tip">已评分，感谢您的反馈</span>
    </div>

    <!-- 评分统计区域 -->
    <div class="rating-stats" v-if="ratingInfo.ratingCount > 0">
      <div class="rating-average">
        <span class="average-score">{{ratingInfo.avgRating || '0.00'}}</span>
        <span class="average-label">课程平均分</span>
      </div>
      <div class="rating-distribution">
        <div
          v-for="item in distributionData"
          :key="item.score"
          class="distribution-item"
        >
          <span class="distribution-label">{{item.label}}</span>
          <div class="distribution-bar">
            <div
              class="distribution-fill"
              :style="{ width: item.percentage + '%' }"
            ></div>
          </div>
          <span class="distribution-count">{{item.count}}人</span>
        </div>
      </div>
    </div>

    <!-- 低分原因弹窗 -->
    <LowRatingModal
      v-model:visible="showLowRatingModal"
      :score="selectedScore"
      :course-id="courseId"
      @success="handleRatingSuccess"
    />
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import LowRatingModal from './lowRatingModal.vue'

export default {
  name: 'CourseRating',
  components: {
    LowRatingModal
  },
  props: {
    courseId: {
      type: [Number, String],
      required: true
    },
    userRating: {
      type: Object,
      default: null
    },
    ratingInfo: {
      type: Object,
      default: () => ({
        avgRating: '0.00',
        ratingCount: 0
      })
    },
    isLoggedIn: {
      type: Boolean,
      default: false
    },
    scoreDistribution: {
      type: Array,
      default: () => []
    }
  },
  emits: ['rating-success', 'update:userRating'],
  setup(props, { emit }) {
    const store = useStore()
    const hoveredScore = ref(0)
    const showLowRatingModal = ref(false)
    const selectedScore = ref(0)
    // const scoreDistribution = ref([])

    const ratingOptions = [
      { score: 5, label: '非常满意', emoji: '😄' },
      { score: 4, label: '比较满意', emoji: '🙂' },
      { score: 3, label: '一般', emoji: '😐' },
      { score: 2, label: '不太满意', emoji: '🙁' },
      { score: 1, label: '非常不满', emoji: '😠' }
    ]

    const getRatingOption = (score) => {
      return ratingOptions.find(item => item.score === score) || ratingOptions[0]
    }

    const distributionData = computed(() => {
      const total = props.ratingInfo.ratingCount || 0
      const dist = props.scoreDistribution || []

      return ratingOptions.map(option => {
        const item = dist.find(d => d.score === option.score) || { count: 0 }
        return {
          ...option,
          count: item.count || 0,
          percentage: total > 0 ? Math.round(((item.count || 0) / total) * 100) : 0
        }
      }).reverse()
    })

    const handleRatingClick = (score) => {
      // 低分(1-3分)需要填写不满原因
      if (score <= 3) {
        selectedScore.value = score
        showLowRatingModal.value = true
      } else {
        // 4-5分直接提交
        submitRating(score, [])
      }
    }

    const submitRating = async (score, reasons) => {
      try {
        await store.dispatch('rating/submitRating', {
          courseId: props.courseId,
          score,
          dissatisfiedReasons: reasons.join(',')
        })
        handleRatingSuccess({ score, dissatisfiedReasons: reasons })
      } catch (error) {
        console.error('评分提交失败', error)
      }
    }

    const handleRatingSuccess = (data) => {
      showLowRatingModal.value = false
      emit('rating-success', data)
      emit('update:userRating', {
        score: data.score,
        dissatisfiedReasons: data.dissatisfiedReasons,
        scoreLabel: getRatingOption(data.score).label
      })
    }

    // 获取评分分布
    // const fetchRatingDistribution = async () => {
    //   try {
    //     const res = await apis.queryAverageRating({
    //       courseId: props.courseId
    //     })
    //     if (res && res.distribution) {
    //       scoreDistribution.value = res.distribution
    //     }
    //   } catch (error) {
    //     console.error('获取评分分布失败', error)
    //   }
    // }

    watch(() => props.courseId, (newVal) => {
      if (newVal) {
        // fetchRatingDistribution()
      }
    }, { immediate: true })

    return {
      hoveredScore,
      showLowRatingModal,
      selectedScore,
      ratingOptions,
      distributionData,
      getRatingOption,
      handleRatingClick,
      handleRatingSuccess
    }
  }
}
</script>

<style lang="scss" scoped>
.course-rating {
  padding: 20px;
  border-top: 1px solid #E7E7E7;
  margin-top: 20px;

  .rating-header {
    display: flex;
    align-items: center;
    margin-bottom: 16px;

    h3 {
      font-size: 18px;
      font-weight: 600;
      color: #333;
      margin: 0;
    }

    .rating-count {
      margin-left: 8px;
      font-size: 14px;
      color: #999;
    }
  }

  .rating-buttons {
    margin-bottom: 20px;

    .rating-label {
      font-size: 14px;
      font-weight: bold;
      color: #666;
      margin-right: 12px;
      margin-bottom: 20px;
      text-indent: 10px;
    }

    .rating-options {
      display: inline-flex;
      gap: 8px;
      margin-left: 10px;
    }

    .rating-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 8px 16px;
      border: 1px solid #E7E7E7;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s;
      min-width: 60px;

      &:hover, &.active {
        border-color: #FD3374;
        background: #FFF0F5;
      }

      &.low-score:hover, &.low-score.active {
        border-color: #FF6B6B;
        background: #FFF5F5;
      }

      .rating-emoji {
        font-size: 24px;
        margin-bottom: 4px;
      }

      .rating-text {
        font-size: 12px;
        color: #666;
      }
    }
  }

  .rating-submitted {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    padding: 12px 16px;
    background: #F5F5F5;
    border-radius: 8px;

    .rating-label {
      font-size: 14px;
      color: #666;
      margin-right: 12px;
    }

    .my-rating {
      display: flex;
      align-items: center;
      padding: 4px 12px;
      background: #FD3374;
      border-radius: 20px;
      color: #fff;

      .rating-emoji {
        font-size: 16px;
        margin-right: 4px;
      }

      .rating-text {
        font-size: 14px;
      }
    }

    .rating-tip {
      margin-left: 12px;
      font-size: 14px;
      color: #21A663;
    }
  }

  .rating-stats {
    display: flex;
    gap: 32px;
    padding: 16px;
    background: #FAFAFA;
    border-radius: 8px;

    .rating-average {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-width: 100px;

      .average-score {
        font-size: 36px;
        font-weight: 600;
        color: #FD3374;
      }

      .average-label {
        font-size: 12px;
        color: #999;
        margin-top: 4px;
      }
    }

    .rating-distribution {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 6px;

      .distribution-item {
        display: flex;
        align-items: center;
        gap: 8px;

        .distribution-label {
          font-size: 12px;
          color: #666;
          width: 60px;
          text-align: right;
        }

        .distribution-bar {
          flex: 1;
          height: 8px;
          background: #E7E7E7;
          border-radius: 4px;
          overflow: hidden;

          .distribution-fill {
            height: 100%;
            background: #FD3374;
            border-radius: 4px;
            transition: width 0.3s;
          }
        }

        .distribution-count {
          font-size: 12px;
          color: #999;
          width: 50px;
        }
      }
    }
  }
}
</style>
